import type { ReactNode } from "react";
import { ChevronDown, Info, Layers, ListOrdered } from "lucide-react";
import { OP_SURFACE } from "@/lib/opSurfaces";
import { cn } from "@/lib/utils";

export type OpSectionRole = "now" | "path" | "optional" | "advanced" | "info";

export function OpSection({
  role,
  step,
  title,
  hint,
  id,
  defaultOpen = false,
  children,
  className,
  actions,
}: {
  role: OpSectionRole;
  step?: number;
  title?: string;
  hint?: string;
  id?: string;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
}) {
  if (role === "info") {
    return (
      <div
        id={id}
        className={cn("space-y-1 px-3 py-2 text-sm", OP_SURFACE.help.box, className)}
      >
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-yellow-700" aria-hidden />
          <div className="min-w-0 flex-1">
            {title ? <p className="text-sm font-medium text-yellow-950">{title}</p> : null}
            {hint ? <p className="text-yellow-900/80">{hint}</p> : null}
            {children}
          </div>
        </div>
      </div>
    );
  }

  if (role === "optional" || role === "advanced") {
    const badge = role === "optional" ? "Opcjonalne" : "Dodatkowe";
    return (
      <details
        id={id}
        className={cn("group open:shadow-sm", OP_SURFACE.optional.box, className)}
        open={defaultOpen}
      >
        <summary className="cursor-pointer list-none px-4 py-3 marker:content-none [&::-webkit-details-marker]:hidden">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-md bg-violet-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-violet-950">
              <Layers className="h-3.5 w-3.5 text-violet-700" aria-hidden />
              {badge}
            </span>
            {title ? <span className="text-sm font-semibold text-violet-950">{title}</span> : null}
            <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-violet-600 transition-transform group-open:rotate-180" />
          </div>
          {hint ? <p className="mt-1 text-xs text-violet-900/80">{hint}</p> : null}
        </summary>
        <div className="space-y-3 border-t border-violet-200 px-4 py-3 text-sm">{children}</div>
      </details>
    );
  }

  const isNow = role === "now";
  return (
    <section
      id={id}
      className={cn(
        "relative overflow-hidden rounded-xl border-2 border-slate-300 bg-white p-4 shadow-md",
        className,
      )}
    >
      <span aria-hidden className="absolute inset-y-0 left-0 w-1.5 bg-brand" />
      <div className="flex flex-wrap items-start justify-between gap-3 pl-3">
        <div className="flex min-w-0 items-start gap-3">
          {typeof step === "number" ? (
            <span
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base font-bold",
                isNow ? "bg-brand text-brand-foreground" : "bg-brand/15 text-brand",
              )}
            >
              {step}
            </span>
          ) : (
            <ListOrdered className="mt-1 h-5 w-5 shrink-0 text-brand" aria-hidden />
          )}
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-brand">
              {isNow ? "Teraz · krok" : "Krok"}
              {typeof step === "number" ? ` ${step}` : ""}
            </p>
            {title ? <h3 className="text-base font-semibold text-slate-900">{title}</h3> : null}
            {hint ? <p className="mt-0.5 text-sm text-muted-foreground">{hint}</p> : null}
          </div>
        </div>
        {actions}
      </div>
      <div className="mt-4 space-y-3 pl-3">{children}</div>
    </section>
  );
}
